import { Controller } from "@hotwired/stimulus"

// Pro Dark palette (standoutdark) – hex values Stripe accepts reliably
const DARK_THEME_COLORS = {
  primary: '#D9B8BC',
  surface: '#232021',
  input: '#3C3032',
  content: '#F8F2F3',
  contentMuted: '#D9B8BC',
  placeholder: '#9A8C98',
  border: '#5F3D41',
  danger: '#E42337',
}

const LIGHT_THEME_COLORS = {
  primary: '#5F3D41',
  surface: '#FFFFFF',
  input: '#FFFFFF',
  content: '#1D1C1C',
  contentMuted: '#5F3D41',
  placeholder: '#9A8C98',
  border: '#D9B8BC',
  danger: '#E42337',
}

export default class extends Controller {
  static targets = ["form", "submit", "error"]
  static values = {
    publishableKey: String,
    clientSecret: String,
    returnUrl: String,
    openedInModal: String
  }

  connect() {
    requestAnimationFrame(() => this.initializeStripe())
  }

  disconnect() {
    if (this.paymentElement) {
      this.paymentElement.clear()
      this.paymentElement = null
    }
  }

  initializeStripe() {
    this.stripe = Stripe(this.publishableKeyValue)
    const isDarkMode = this.#isDarkMode()
    const colors = isDarkMode ? DARK_THEME_COLORS : LIGHT_THEME_COLORS

    const options = {
      mode: 'setup',
      currency: 'sek',
      appearance: {
        theme: 'flat',
        variables: {
          colorPrimary: colors.primary,
          colorBackground: colors.surface,
          colorText: colors.content,
          colorTextSecondary: colors.contentMuted,
          colorTextPlaceholder: colors.placeholder,
          colorDanger: colors.danger,
          iconChevronDownColor: colors.content,
          iconChevronDownHoverColor: colors.primary,
          fontFamily: 'system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '6px',
        },
        rules: {
          '.Block': {
            backgroundColor: colors.surface,
            border: 'none',
            boxShadow: 'none',
          },
          '.AccordionItem': {
            backgroundColor: colors.surface,
            border: 'none',
            boxShadow: 'none',
          },
          '.Input': {
            backgroundColor: colors.input,
            border: `1px solid ${colors.border}`,
            color: colors.content,
            boxShadow: 'none',
          },
          '.Input:focus': {
            border: `1px solid ${colors.primary}`,
            boxShadow: 'none',
          },
          '.Label': {
            color: colors.content,
          },
          '.Text': {
            color: colors.contentMuted,
          },
          '.Tab': {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.content,
          },
          '.Tab--selected': {
            backgroundColor: colors.input,
            border: `1px solid ${colors.primary}`,
            color: colors.content,
          },
          '.Dropdown': {
            border: `1px solid ${colors.border}`,
          },
          '.DropdownItem': {
            backgroundColor: colors.input,
            color: colors.content,
          },
          '.PickerItem': {
            backgroundColor: colors.input,
            color: colors.content,
          },
        },
      },
    }

    this.elements = this.stripe.elements(options)
    this.paymentElement = this.elements.create('payment')
    this.paymentElement.mount('#payment-element')
    this.setupFormListener()
  }

  setupFormListener() {
    if (!this.hasFormTarget) return

    this.formTarget.addEventListener('submit', this.handleSubmit.bind(this))
  }

  async handleSubmit(event) {
    event.preventDefault()

    if (this.submitTarget.disabled) {
      return
    }

    this.submitTarget.disabled = true

    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        this.handleError(submitError)
        return
      }

      const { error } = await this.stripe.confirmSetup({
        elements: this.elements,
        clientSecret: this.clientSecretValue,
        confirmParams: {
          return_url: this.returnUrlValue,
        },
      })

      if (error) {
        this.handleError(error)
      }
    } catch (error) {
      this.handleError(error)
    }
  }

  handleError(error) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = error.message
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
    }
  }

  #isDarkMode() {
    return document.documentElement.dataset.theme === 'standoutdark'
  }
}
